import type {
  Content,
  CustomTableLayout,
  TableCell,
  TDocumentDefinitions,
} from 'pdfmake/interfaces.js';
import { ltUtcText, zonedStamp } from '../../inputs/lt-utc.js';
import { pdfBrandLogo } from '../../pdf-brand.js';
import { TOKENS } from '../../tokens.js';
import type { ReportBrand } from '../voyage/source.js';
import { equipmentLabel } from './routes.js';
import type { DefectDetail, DefectSignature } from './types.js';

/** Çevirici: i18next `t`. */
export type DefectPdfT = (key: string, options?: Record<string, unknown>) => string;

export interface DefectPdfInput {
  defect: DefectDetail;
  vessel: { name: string; imo?: string | null };
  brand: ReportBrand | null;
  /** İlgili belgelerin adları (ekler). */
  documents?: readonly string[];
  /** Basım zamanı (UTC ISO). */
  printedAt: string;
}

const FONT_SIZE = 9;
const LINES: CustomTableLayout = {
  hLineWidth: (i) => (i === 1 ? 0.8 : 0.4),
  vLineWidth: () => 0,
  hLineColor: () => TOKENS.border,
  paddingLeft: () => 4,
  paddingRight: () => 4,
  paddingTop: () => 3,
  paddingBottom: () => 3,
  fillColor: (row) => (row === 0 ? TOKENS['table-head-bg'] : null),
};
const FACTS: CustomTableLayout = { ...LINES, fillColor: () => null, hLineWidth: () => 0.4 };

const text = (value: unknown) => (typeof value === 'string' && value.trim() ? value.trim() : '—');

/** `YYYY-MM-DD` → `DD.MM.YYYY`. */
const day = (value: string | null | undefined) =>
  value && /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? `${value.slice(8, 10)}.${value.slice(5, 7)}.${value.slice(0, 4)}`
    : '—';

/**
 * Arıza Bildirim Formu LGS-17'nin PDF'i (ADR 0096 §10; ADR 0060 §12 ve ADR 0057 §7 dili): A4
 * dikey; şirket adı ve logo, "Defect Report (LGS-17)", kayıt no ve aşama; gemi ve kaynak; Part A
 * (mürettebat), Part B (kaptan), Part C (işletme), SMS takibi ve imzalar (imza yerine ad, rol,
 * zaman LT + UTC; ortağın cevabı #9); ekte bağlı kayıtlar, ayrıntılar (inceleme ve kapanış),
 * faaliyetler, takip geçmişi, ilgili belgeler; her sayfada basım zamanı ve sayfa no. Saf işlev:
 * gemi ve ofis aynı tanımı kullanır.
 */
export function defectPdf(t: DefectPdfT, input: DefectPdfInput): TDocumentDefinitions {
  const { defect, vessel } = input;
  const d = (key: string, options?: Record<string, unknown>) => t(`defects.${key}`, options);
  const f = (key: string) => d(`record.fields.${key}`);
  const offset = defect.tzOffsetMin ?? 0;
  const time = (value: string | null | undefined) => (value ? ltUtcText(value, offset) : '—');
  const logo = pdfBrandLogo(input.brand, { fit: [48, 48], width: 56 });
  const title = d('record.title');

  const heading = (value: string): Content => ({ text: value, style: 'section' });
  const facts = (pairs: [string, string][]): Content => {
    const body: TableCell[][] = [];
    for (let i = 0; i < pairs.length; i += 2) {
      const [a, b] = [pairs[i], pairs[i + 1]];
      body.push([
        { text: a?.[0] ?? '', style: 'label' },
        { text: a?.[1] ?? '' },
        { text: b?.[0] ?? '', style: 'label' },
        { text: b?.[1] ?? '' },
      ]);
    }
    return { table: { widths: [100, '*', 100, '*'], body }, layout: FACTS };
  };
  const table = (head: string[], body: string[][], widths: (string | number)[]): Content => ({
    table: { headerRows: 1, widths, body: [head.map((h) => ({ text: h, bold: true })), ...body] },
    layout: LINES,
  });
  const paragraph = (value: string | null | undefined): Content => ({
    text: text(value),
    margin: [0, 0, 0, 2],
  });

  const content: Content[] = [];
  const marks = defect.stage === 'closed' ? [] : [d('pdf.notClosed')];
  content.push({
    columns: [
      ...(logo ? [logo] : []),
      {
        width: '*',
        stack: [
          { text: input.brand?.name ?? '', style: 'company' },
          { text: d('lgs.eyebrow'), style: 'title' },
          { text: d('pdf.reportNo', { no: defect.defectNo }), style: 'muted' },
        ],
      },
      {
        width: 'auto',
        stack: [
          {
            text:
              defect.awaitingParts && defect.stage === 'under_action'
                ? d('awaitingParts')
                : d(`stages.${defect.stage}`),
            style: 'stage',
            alignment: 'right' as const,
          },
          ...marks.map((mark) => ({ text: mark, style: 'mark', alignment: 'right' as const })),
        ],
      },
    ],
    columnGap: 10,
    margin: [0, 0, 0, 8],
  });

  // Genel bilgiler: gemi, kayıt, kaynak ve aşama (LGS-17 başlığı)
  const reporter = defect.reportedBy;
  const l = (key: string, options?: Record<string, unknown>) => d(`lgs.${key}`, options);
  const ref = defect.sourceRef ?? null;
  const sourceText =
    ref?.kind === 'hse_report'
      ? l('sourceHse', {
          type: t(`hse.types.${ref.reportType ?? ''}`, { defaultValue: ref.reportType ?? '' }),
          no: ref.no ?? '',
        })
      : ref?.kind === 'work_order'
        ? l('sourceWorkOrder', { no: ref.no ?? '' })
        : ref?.kind === 'daily_report'
          ? l('sourceDaily', { no: day(ref.no) })
          : defect.source === 'manual'
            ? l('sourceDirect')
            : d(`record.sources.${defect.source}`);
  content.push(
    facts([
      [f('vessel'), vessel.name],
      [f('imo'), text(vessel.imo ?? null)],
      [f('recordNo'), defect.defectNo],
      [f('dateReported'), time(defect.reportedAt)],
      [f('source'), sourceText],
      [
        d('list.columns.stage'),
        defect.awaitingParts && defect.stage === 'under_action'
          ? d('awaitingParts')
          : d(`stages.${defect.stage}`),
      ],
    ]),
  );

  // PART A – mürettebat (LGS-17)
  content.push(
    heading(`${l('parts.a')} (${l('parts.aHint')})`),
    facts([
      [l('fields.crewName'), text(reporter.name)],
      [l('fields.position'), text(reporter.roleName)],
      [l('fields.equipment'), equipmentLabel(defect)],
      [l('fields.location'), text(defect.location)],
    ]),
    { text: l('fields.description'), style: 'label', margin: [0, 4, 0, 1] },
    paragraph(defect.description),
    { text: l('fields.actionRequired'), style: 'label', margin: [0, 4, 0, 1] },
    paragraph(defect.actionRequired ?? null),
  );

  // PART B – kaptan
  content.push(
    heading(`${l('parts.b')} (${l('parts.bHint')})`),
    { text: l('fields.actionTaken'), style: 'label', margin: [0, 0, 0, 1] },
    paragraph(defect.immediateAction),
    facts([[l('fields.dateRectified'), day(defect.rectifiedOn)]]),
  );
  // Part B'yi yazan ve rütbesi; kaptan değilse "On behalf of Master" (ADR 0127 §4)
  const partB = defect.partBAuthor;
  if (partB) {
    content.push({
      text: `${t('masterSection.writtenBy', {
        name: text(partB.name),
        rank: partB.roleName ?? t('masterSection.noRank'),
        at: ltUtcText(partB.at, partB.tzOffsetMin),
      })}${partB.onBehalf ? ` · ${t('masterSection.onBehalf')}` : ''}`,
      style: 'label',
      margin: [0, 2, 0, 0],
    });
  }

  // PART C – işletme (ofisin yönetim faaliyeti notları; ADR 0060 §6)
  const management = defect.officeNotes.filter((n) => n.kind === 'management_action');
  content.push(heading(`${l('parts.c')} (${l('parts.cHint')})`));
  if (management.length === 0) content.push(paragraph(null));
  for (const note of management) {
    content.push({
      text: [
        { text: `${day(note.actionOn)} · ${text(note.byName)}: `, style: 'label' },
        { text: note.body },
      ],
      margin: [0, 0, 0, 2],
    });
  }

  // SMS takibi ve imzalar (imza yerine ad, rol, zaman; ortağın cevabı #9)
  const review = defect.procedureReview;
  const who = (s: DefectSignature | null | undefined, verb: string) =>
    s
      ? `${text(s.name)} (${text(s.roleName)}) · ${d(`signatures.${verb}`, { at: time(s.at) })}`
      : '—';
  const steps = defect.signatures.steps;
  const officeStep = steps.find((s) => s.office);
  const shipStep = steps.find((s) => !s.office);
  const stepText = (step: (typeof steps)[number] | undefined) => {
    if (!defect.signatures.submission) return d('signatures.notSubmitted');
    if (!step) return d('signatures.notRequired');
    if (!step.signature) return d('signatures.pending');
    return who(step.signature, step.decision === 'returned' ? 'returned' : 'approved');
  };
  content.push(
    heading(l('parts.sms')),
    facts([
      [
        l('fields.procedures'),
        review ? (review.required ? text(review.procedure) : l('fields.noUpdate')) : '—',
      ],
      [l('fields.improvement'), text(review?.improvement ?? null)],
    ]),
    {
      table: {
        widths: [130, '*'],
        body: [
          [
            { text: l('fields.crewSignature'), style: 'label' },
            {
              stack: [
                who(reporter, 'reported'),
                defect.signatures.submission
                  ? who(defect.signatures.submission, 'submitted')
                  : d('signatures.notSubmitted'),
              ],
            },
          ],
          [{ text: l('fields.captainSignature'), style: 'label' }, stepText(shipStep)],
          [{ text: l('fields.managementApproval'), style: 'label' }, stepText(officeStep)],
        ],
      },
      layout: FACTS,
      margin: [0, 4, 0, 0],
    },
    { text: d('signatures.note'), style: 'muted', margin: [0, 4, 0, 0] },
  );

  // Ek: bağlı kayıtlar (ADR 0096 §3)
  const links = defect.links;
  const linked: string[][] = [
    ...(links?.workOrders ?? []).map((w) => [
      l('side.kinds.workOrder'),
      w.woNo,
      text(w.title),
      l(`workOrders.statuses.${w.status}`, { defaultValue: w.status }),
    ]),
    ...(links?.nonConformities ?? []).map((n) => [
      l('side.kinds.nonConformity'),
      n.reportNo,
      text(n.title),
      t(`hse.statuses.${n.status}`, { defaultValue: n.status }),
    ]),
    ...(links?.requisitions ?? []).map((r) => [
      l('side.kinds.requisition'),
      r.requisitionNo,
      day(r.requestDate),
      l(`material.reqStatuses.${r.status}`, { defaultValue: r.status }),
    ]),
  ];
  if (linked.length > 0) {
    content.push(
      heading(l('side.related')),
      table(['', '#', '', d('list.columns.stage')], linked, [110, 80, '*', 80]),
    );
  }

  // Ek: ayrıntılar (tür, ciddiyet, öncelik, inceleme ve kapanış; K24)
  content.push(
    heading(l('more')),
    facts([
      [f('kind'), d(`kinds.${defect.kind}`)],
      [f('severity'), d(`severities.${defect.severity}`)],
      [f('priority'), defect.priority ? d(`priorities.${defect.priority}`) : '—'],
      [f('department'), defect.department ? d(`departments.${defect.department}`) : '—'],
      [f('targetOn'), day(defect.targetOn)],
      [f('captainActionOn'), day(defect.captainActionOn)],
      [
        f('job'),
        defect.job ? [defect.job.code, defect.job.title].filter(Boolean).join(' · ') : '—',
      ],
      [f('operationalImpact'), defect.operationalImpact ? f('yes') : f('no')],
      [f('investigation'), text(defect.investigation)],
      [f('rootCause'), text(defect.rootCause)],
      [f('correctiveAction'), text(defect.correctiveAction)],
      [f('repairWorkOrder'), text(defect.repairWorkOrder?.woNo ?? null)],
      [f('noRepairReason'), text(defect.noRepairReason)],
      [f('testResult'), text(defect.testResult)],
      [f('conditionAfter'), defect.conditionAfter ? d(`conditions.${defect.conditionAfter}`) : '—'],
      [
        f('classNotificationRef'),
        text(defect.classNotificationRef ?? defect.classNotificationWaiver),
      ],
      [f('closedOn'), defect.closedAt ? time(defect.closedAt) : '—'],
      [f('category'), text(defect.equipment?.category ?? null)],
    ]),
  );

  // Düzeltici faaliyetler
  if (defect.actions.length > 0) {
    const c = (key: string) => d(`actions.columns.${key}`);
    content.push(
      heading(d('record.cards.actions')),
      table(
        [
          c('no'),
          c('description'),
          c('responsible'),
          c('targetOn'),
          c('status'),
          c('completedOn'),
          c('evidence'),
        ],
        defect.actions.map((a, i) => [
          String(i + 1),
          a.description,
          text(a.responsible),
          day(a.targetOn),
          d(`actionStatuses.${a.status}`),
          day(a.completedOn),
          a.status === 'cancelled' ? text(a.cancelReason) : text(a.evidence),
        ]),
        [14, '*', 70, 52, 52, 52, 90],
      ),
    );
  }

  // Takip geçmişi (gemi ve ofis notları zamana göre)
  const history = [
    ...defect.followups.map((n) => ({
      at: n.at,
      offset: n.tzOffsetMin,
      body: n.body,
      by: n.byName,
    })),
    ...defect.officeNotes
      .filter((n) => n.kind === 'comment')
      .map((n) => ({
        at: n.at,
        offset: 0,
        body: n.body,
        by: `${text(n.byName)} (${d('followups.office')})`,
      })),
  ].sort((a, b) => a.at.localeCompare(b.at));
  content.push(heading(d('record.cards.followups')));
  if (history.length === 0) {
    content.push({ text: d('followups.empty'), style: 'muted' });
  } else {
    content.push(
      table(
        [d('followups.columns.at'), d('followups.columns.remarks'), d('followups.columns.by')],
        history.map((h) => [`${zonedStamp(h.at, h.offset)}`, h.body, text(h.by)]),
        [80, '*', 110],
      ),
    );
  }

  // İlgili belgeler
  if ((input.documents?.length ?? 0) > 0) {
    content.push(heading(d('record.cards.related')), {
      ul: [...(input.documents ?? [])],
    });
  }

  const printed = d('pdf.draftCopy', { at: `${zonedStamp(input.printedAt)} UTC` });
  return {
    pageSize: 'A4',
    pageOrientation: 'portrait',
    pageMargins: [36, 36, 36, 44],
    defaultStyle: { font: 'Roboto', fontSize: FONT_SIZE, color: TOKENS['navy-900'] },
    info: { title: `${title} ${defect.defectNo}`, subject: vessel.name, creator: 'Felixmate' },
    styles: {
      company: { fontSize: 10, bold: true, color: TOKENS['text-secondary'] },
      title: { fontSize: 15, bold: true, margin: [0, 2, 0, 2] },
      section: { fontSize: 10.5, bold: true, margin: [0, 10, 0, 4] },
      label: { color: TOKENS['text-secondary'] },
      muted: { color: TOKENS['text-secondary'], fontSize: 8 },
      stage: { bold: true, fontSize: 10 },
      mark: { bold: true, color: TOKENS['danger-fg'], fontSize: 9 },
    },
    content,
    footer: (page: number, pages: number) => ({
      columns: [
        {
          text: `${defect.defectNo} · ${printed} · Felixmate`,
          fontSize: 7,
          color: TOKENS['text-secondary'],
        },
        {
          text: d('pdf.page', { page, pages }),
          alignment: 'right',
          fontSize: 7,
          color: TOKENS['text-secondary'],
        },
      ],
      margin: [36, 12, 36, 0],
    }),
  };
}

/** Dosya adı: "DEF-2026-0018 Defect Report.pdf". */
export function defectPdfFileName(defectNo: string, title: string): string {
  return `${`${defectNo} ${title}`.replace(/[^A-Za-z0-9 ._-]+/g, '-')}.pdf`;
}
